// Copyright (c) StrangeBee 2023

import { ReactElement } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import {
    TaskStatus,
    ObjectTypeV1,
    QuerySelector,
    AttachmentType,
    CaseOutput,
    PermissionsEnum,
    TaskLogOutput,
    AttachmentCaseType,
    CaseReportAttachmentOutput,
} from 'thehive-sdk';
import { THLayout } from '@Molecules/THLayout';
import { THList } from '@Molecules/THList';
import useConfigList from '@Hooks/useConfigList';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { computeEventList } from '@Organisms/Case/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { AttachmentTasksList } from './AttachmentTasksList';
import { AttachmentCasesList } from './AttachmentCasesList';
import { AttachmentsCreate } from '@Organisms/Attachments/AttachmentsCreate/AttachmentsCreate';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ICONS } from '@Constants/iconsApp';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { useDrawer } from '@Hooks/useDrawer';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Case/selector';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { AttachmentReportsList } from './AttachmentReportsList';
import { THTabs } from '@Molecules/Inputs/THTabs/THTabs';
import { usePreview, THPreview } from '@Molecules/THPreview';

import './CaseAttachmentList.scss';

const checkLocation = (location: string, caseId: string): string =>
    ['cases', 'tasks', 'reports'].reduce((acc, sectionId) => {
        const path =
            sectionId === 'cases'
                ? RouteBuilder.compute('cases.attachments', { caseId })
                : RouteBuilder.compute('cases.attachments-section', { caseId, sectionId });
        return location.includes(path) ? path : acc;
    });

const computeSelectorTasksLogs = (caseId: string): QuerySelector => ({
    head: [
        { name: 'getCase', idOrName: caseId },
        { name: 'tasks' },
        { filter: { field: 'status', values: TaskStatus.CANCEL, operator: 'not', type: 'string' } },
        { name: 'logs' },
        { filter: { operator: 'contains', values: 'attachments.id', type: 'string' } },
    ],
    extraData: ['task', 'taskId'],
    name: 'get-case-logs-attachments',
});

const computeSelectorCaseReports = (caseId: string): QuerySelector => ({
    head: [{ name: 'getCase', idOrName: caseId }, { name: 'caseReports' }],
    name: 'get-case-reports',
    sort: ['-_createdAt'],
});

const computeSelectorCasesAttachments = (caseId: string): QuerySelector => ({
    head: [{ name: 'getCase', idOrName: caseId }, { name: 'attachments' }],
    name: 'get-case-attachments',
});

const CaseAttachmentList = (): ReactElement | null => {
    const i = useLocalMessage();
    const history = useHistory();
    const location = useLocation();

    const { caseId, sectionId = 'cases' } = useParams<{ caseId: string; sectionId: 'cases' | 'tasks' | 'reports' }>();
    const { data: caseData } = useOneQuery<CaseOutput>({
        controller: client.CaseController,
        selector: computeGetSelector({ caseId }),
    });
    const { userPermissions = [] } = caseData || {};
    const hasPermissions = useSplitPermissions(userPermissions, [PermissionsEnum.MANAGE_CASE_UPDATE]);

    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<AttachmentType>({});

    const { togglePreview, content, type } = usePreview();

    const {
        dataSource,
        paginationManager,
        table,
        listRefetch,
        countRefetch,
        status = 'loading',
    } = useConfigList<AttachmentCaseType | TaskLogOutput | CaseReportAttachmentOutput>({
        selector:
            sectionId === 'cases'
                ? computeSelectorCasesAttachments(caseId)
                : sectionId === 'tasks'
                ? computeSelectorTasksLogs(caseId)
                : computeSelectorCaseReports(caseId),
        controller: client.CaseController,
        storageId: StorageFilterEnum.CASE_ATTACHMENTS,
        objectType: ObjectTypeV1.CASE,
        streamRootId: caseId,
    });

    const eventList = computeEventList({
        controller: client.CaseController,
        locale: i,
        onError: handleNotificationError,
        onSuccess: (_, msg) => {
            openMessage('success', msg);
            listRefetch();
            countRefetch();
        },
    });

    const AttachmentList =
        sectionId === 'cases' ? (
            <AttachmentCasesList
                status={status}
                hasPermissions={hasPermissions.updateCase}
                dataSource={dataSource as AttachmentCaseType[]}
                caseId={caseId}
                getDownloadUrl={(attachmentId) =>
                    window.location.origin + client.apiUrl + `/v1/case/${caseId}/attachment/${attachmentId}/download`
                }
                onDelete={({ id }) =>
                    eventList.attachment
                        .onDelete(caseId, id)
                        .then(() => {
                            i('attachment.action.success.delete');
                            listRefetch();
                        })
                        .catch(handleNotificationError)
                }
                handlePreview={togglePreview}
            />
        ) : sectionId === 'tasks' ? (
            <AttachmentTasksList
                dataSource={dataSource as TaskLogOutput[]}
                hasPermissions={hasPermissions.updateCase}
                caseId={caseId}
                status={status}
                getDownloadUrl={(attachmentId, logId) =>
                    window.location.origin + client.apiUrl + `/v1/log/${logId}/attachment/${attachmentId}/download`
                }
                handlePreview={togglePreview}
            />
        ) : (
            <AttachmentReportsList
                onDelete={({ id }) => {
                    eventList
                        .onDeleteReport(id)
                        .then(() => i('attachment.action.success.delete'))
                        .catch(handleNotificationError);
                }}
                dataSource={dataSource as CaseReportAttachmentOutput[]}
                hasPermissions={hasPermissions.updateCase}
                status={status}
                getDownloadUrl={(reportId) =>
                    window.location.origin + client.apiUrl + `/v1/caseReport/${reportId}/download`
                }
            />
        );

    return (
        <>
            {content && type && <THPreview src={content} onClick={togglePreview} type={type} />}

            <THLayout>
                <THList>
                    <THList.Header className="case-attachment-header">
                        <THList.Slot name="CONTENT" className="case-attachment-header__content">
                            <THTabs
                                defaultActiveKey={checkLocation(location.pathname, caseId)}
                                type="card"
                                size="small"
                                onTabClick={(key) => history.push(key)}
                            >
                                <THTabs.TabPane
                                    tab={i('case.attachment.tab.cases')}
                                    key={RouteBuilder.compute('cases.attachments-section', {
                                        caseId,
                                        sectionId: 'cases',
                                    })}
                                >
                                    {hasPermissions && (
                                        <AddButton
                                            label={i('attachment.sider.add')}
                                            className="case-attachment-header__btn-add"
                                            onClick={() => setVisibleCreate(true)}
                                        />
                                    )}
                                </THTabs.TabPane>
                                <THTabs.TabPane
                                    tab={i('case.attachment.tab.tasks')}
                                    key={RouteBuilder.compute('cases.attachments-section', {
                                        caseId,
                                        sectionId: 'tasks',
                                    })}
                                />
                                <THTabs.TabPane
                                    tab={i('case.attachment.tab.reports')}
                                    key={RouteBuilder.compute('cases.attachments-section', {
                                        caseId,
                                        sectionId: 'reports',
                                    })}
                                >
                                    {hasPermissions && (
                                        <AddButton
                                            label={i('attachment.create.report.title')}
                                            className="case-attachment-header__btn-add"
                                            onClick={() => setVisibleCreate(true)}
                                        />
                                    )}
                                </THTabs.TabPane>
                            </THTabs>
                        </THList.Slot>
                    </THList.Header>

                    {AttachmentList}

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>
            </THLayout>

            {visibleCreate && sectionId === 'cases' && (
                <AttachmentsCreate
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onAddAttachment={(formData) => eventList.attachment.onAdd(caseId, formData)}
                />
            )}

            {visibleCreate && sectionId === 'reports' && (
                <AttachmentsCreate
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onAddAttachment={(formData) => eventList.onUploadReport(formData, caseId)}
                    attachmentTab="reports"
                />
            )}
        </>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.attachments',
        icon: <ICONS.FileSearchOutlined />,
    },
    path: RouteBuilder.compute('cases.attachments'),
    content: <CaseAttachmentList />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.attachments' },
};

export const RouteAttachment: RouteType = {
    path: RouteBuilder.compute('cases.attachments-section'),
    content: <CaseAttachmentList />,
    space: ['user'],
    exact: true,
};
